import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddTemplate from "./AddTemplate";
import EditTemplate from "./EditTemplate";
import "../css/ParticipantList.css";
import "../css/TemplateList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashAlt, faEye, faEyeSlash, faPlus, faEnvelope, faLock, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import DeleteConfirmModal, { useDeleteConfirm } from "./DeleteConfirmModal";
import { GoldLoader } from "../shared/Loaders";

const TemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showAdd, setShowAdd] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [activeCompetitionId, setActiveCompetitionId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const deleteConfirm = useDeleteConfirm();
  const API_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  const stripHtml = (html) => {
    let div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/templates`);
      const data = res.data?.data || res.data?.templates || res.data;
      setTemplates(Array.isArray(data) ? data : []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to fetch templates", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveCompetition = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/competitions?active=true`);
      const competition = res.data?.data?.[0] || res.data?.[0];
      if (competition?._id) setActiveCompetitionId(competition._id);
    } catch (err) {
      console.error("Failed to fetch active competition", err);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchActiveCompetition();
  }, []);

  const handleDelete = (template) => {
    deleteConfirm.ask({ id: template._id, name: template.name || "this template" });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await axios.delete(`${API_URL}/ems/templates/${id}`);
      deleteConfirm.clear();
      fetchTemplates();
    } catch (err) {
      console.error(err);
      deleteConfirm.setError("Delete failed");
      deleteConfirm.setBusy(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleEdit = (template) => {
    setEditTemplate(template);
  };

  const handleSendEmail = () => {
    navigate("/ems/send-emails");
  };

  const toggleView = () => {
    const next = view === "permanent" ? "all" : "permanent";
    setView(next);
    setCurrentPage(1);
    setExpandedId(null);
    setEditTemplate(null);
    setShowAdd(false);
  };

  const isPermanentView = view === "permanent";

  const viewTemplates = templates.filter((t) =>
    isPermanentView ? t.isPermanent === true : true
  );

  const filteredTemplates = viewTemplates.filter((t) =>
    (t.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.subject || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasAnyInView = viewTemplates.length > 0;

  const emptyMessage = isPermanentView
    ? !hasAnyInView
      ? "No permanent templates available"
      : "No permanent templates match your search"
    : "No templates available";

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sorted = sortConfig.key
    ? [...filteredTemplates].sort((a, b) => {
        const aVal = (a[sortConfig.key] || '').toLowerCase();
        const bVal = (b[sortConfig.key] || '').toLowerCase();
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filteredTemplates;

  // Pagination calculations
  const effectivePerPage = itemsPerPage >= sorted.length ? Math.max(sorted.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sorted.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentTemplates = sorted.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  if (loading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="ems-communication-page p-4">
      {/* Header with Search on left and Buttons on right */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 ems-list-toolbar">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control form-control-sm search-input"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search templates"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {view !== "permanent" && (
            <button
              className="btn btn-success"
              onClick={() => setShowAdd(true)}
              style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Template
            </button>
          )}
          <button
            className="btn btn-success"
            onClick={handleSendEmail}
            style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Send Email
          </button>
          <button
            className={`btn ${view === "permanent" ? "btn-warning" : "btn-outline-warning"}`}
            onClick={toggleView}
            style={{ borderRadius: "5px", padding: "8px 12px" }}
          >
            <FontAwesomeIcon icon={faLock} className="me-2" />
            Permanent Templates
          </button>
        </div>
      </div>

      {/* Table */}
        <div className="template-table-header">
          <div
            className={`sort-header ${sortConfig.key === 'name' ? 'sorted' : ''}`}
            onClick={() => toggleSort('name')}
          >
            Template Name <FontAwesomeIcon icon={sortIcon('name')} className="sort-icon" />
          </div>
          <div
            className={`sort-header ${sortConfig.key === 'subject' ? 'sorted' : ''}`}
            onClick={() => toggleSort('subject')}
          >
            Subject <FontAwesomeIcon icon={sortIcon('subject')} className="sort-icon" />
          </div>
          <div>Actions</div>
        </div>

        {loading ? (
          <GoldLoader label="Loading..." variant="section" />
        ) : sorted.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {emptyMessage}
          </div>
        ) : (
          currentTemplates.map((t) => (
            <React.Fragment key={t._id}>
              <div className="template-table-row">
                <div>{t.name || "-"}</div>
                <div>{t.subject || "-"}</div>
                <div className="template-actions">
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => toggleExpand(t._id)}
                    title={expandedId === t._id ? "Hide Details" : "View Details"}
                  >
                    <FontAwesomeIcon icon={expandedId === t._id ? faEyeSlash : faEye} />
                  </button>
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => handleEdit(t)}
                    title="Edit Template"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  {!isPermanentView && (
                    <button
                      className="btn btn-sm action-btn"
                      onClick={() => handleDelete(t)}
                      title="Delete Template"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === t._id && (
                <div className="template-details p-3 border rounded mb-2" style={{ fontSize: "12px", background: "rgb(216, 231, 224)" }}>
                  <p><strong>Subject:</strong> {t.subject || "-"}</p>
                  <p><strong>Template Key:</strong> {t.templateKey || "-"}</p>
                  <p><strong>From Name:</strong> {t.from_name || "-"}</p>
                  <p><strong>From Email:</strong> {t.from_email || "-"}</p>
                  <p>
                    <strong>Body:</strong>
                    <pre style={{ whiteSpace: "pre-wrap" }}>{t.body ? stripHtml(t.body) : "-"}</pre>
                  </p>
                  <p>
                    <strong>Attachments:</strong>
                    {t.attachments?.length > 0
                      ? t.attachments.map((f, i) => (
                          <div key={i}>
                            <a href={f.url || f.path} target="_blank" rel="noopener noreferrer">
                              {f.filename || `File ${i + 1}`}
                            </a>
                          </div>
                        ))
                      : "-"}
                  </p>
                  <p><strong>Created At:</strong> {t.createdAt ? new Date(t.createdAt).toLocaleString() : "-"}</p>
                  <p><strong>Updated At:</strong> {t.updatedAt ? new Date(t.updatedAt).toLocaleString() : "-"}</p>
                </div>
              )}
            </React.Fragment>
          ))
        )}

        {/* Pagination inside table */}
        {sorted.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: '13px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {/* AddTemplate Modal */}
      {showAdd && (
        <AddTemplate
          isOpen={showAdd}
          onClose={() => setShowAdd(false)}
          onSaved={fetchTemplates}
          activeCompetitionId={activeCompetitionId}
        />
      )}

      {/* EditTemplate Modal */}
      {editTemplate && (
        <EditTemplate
          templateData={editTemplate}
          isOpen={!!editTemplate}
          onClose={() => setEditTemplate(null)}
          onSaved={fetchTemplates}
          activeCompetitionId={activeCompetitionId}
        />
      )}
      {deleteConfirm.pending && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}
    </div>
  );
};

export default TemplateList;
